/**
 * A1 · EL FORMATO DE URL DE LOS FILTROS. PURO. Uno solo para TODAS las
 * pantallas.
 *
 * EL CRITERIO, y por qué cada parte:
 *
 *  1. **Query params planos**, un parámetro por filtro, con el nombre canónico
 *     del filtro: `?estado=ASIGNADO&rango=hoy`. Nada de JSON codificado ni
 *     abreviaturas. La URL tiene que poder leerse y editarse a mano — es lo que
 *     permite que alguien la arme por su cuenta y que el copiloto (Fase B4)
 *     genere enlaces sin una librería.
 *
 *  2. **Ausente = apagado.** Un filtro en su valor por defecto NO se escribe.
 *     De acá sale la garantía de compatibilidad: hoy ninguna pantalla lleva
 *     filtros en la URL, así que TODOS los enlaces existentes son "sin
 *     parámetros" — y una URL sin parámetros es exactamente el estado por
 *     defecto. No hay nada que migrar y nada que romper.
 *
 *  3. **Los booleanos por PRESENCIA** (`?vencidos=1`), no por valor.
 *     `vencidos=false` es ruido: alarga la URL y confunde al leerla.
 *
 *  4. **Orden estable**, el del esquema. Dos personas con los mismos filtros
 *     tienen que producir la MISMA URL, o los enlaces compartidos dejan de ser
 *     comparables y el historial del navegador se llena de duplicados.
 *
 *  5. **El esquema es una allowlist.** Solo se serializa lo declarado. Sin eso,
 *     cualquier estado interno del componente (un modal abierto, una fila
 *     seleccionada) terminaría en la URL.
 */

export type TipoFiltro = 'texto' | 'booleano' | 'numero';

export interface CampoFiltro {
  tipo: TipoFiltro;
  /** Solo para `numero`: el valor que NO se escribe en la URL. */
  porDefecto?: number;
}

/** Declaración de los filtros de una pantalla. El orden define el de la URL. */
export type EsquemaFiltros = Record<string, CampoFiltro>;

/** Los valores de los filtros de una pantalla. */
export type Filtros = Record<string, string | boolean | number | undefined>;

/**
 * Filtros → `URLSearchParams`. PURO.
 *
 * Recorre el ESQUEMA, no el objeto de filtros: así el orden es estable y las
 * claves no declaradas quedan afuera.
 */
export function aParams(filtros: Filtros, esquema: EsquemaFiltros): URLSearchParams {
  const params = new URLSearchParams();

  for (const [clave, campo] of Object.entries(esquema)) {
    const valor = filtros[clave];
    if (valor === undefined || valor === null) continue;

    if (campo.tipo === 'booleano') {
      if (valor === true) params.set(clave, '1');
      continue;
    }

    if (campo.tipo === 'numero') {
      const n = Number(valor);
      if (!Number.isFinite(n)) continue;
      if (campo.porDefecto !== undefined && n === campo.porDefecto) continue;
      params.set(clave, String(n));
      continue;
    }

    const texto = String(valor).trim();
    if (texto) params.set(clave, texto);
  }

  return params;
}

/**
 * `URLSearchParams` → filtros. PURO.
 *
 * Tolerante con la entrada: parámetros desconocidos se ignoran (alguien pega un
 * enlace con `utm_source`, o una versión futura agrega un filtro que esta no
 * conoce), y un valor repetido toma el primero.
 */
export function desdeParams(params: URLSearchParams, esquema: EsquemaFiltros): Filtros {
  const out: Filtros = {};

  for (const [clave, campo] of Object.entries(esquema)) {
    const crudo = params.get(clave);

    if (campo.tipo === 'booleano') {
      // Ausente es `false`, no `undefined`: si no, cada pantalla tendría que
      // poner `?? false` por su cuenta. Se resuelve una vez acá.
      out[clave] = crudo === '1';
      continue;
    }

    if (campo.tipo === 'numero') {
      const n = crudo === null ? NaN : Number(crudo);
      out[clave] = Number.isFinite(n) ? n : campo.porDefecto;
      continue;
    }

    if (crudo !== null && crudo.trim()) out[clave] = crudo;
  }

  return out;
}
